import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  QrCode, Store, Clock, CheckCircle, AlertCircle, 
  ShoppingBag, Phone, Search, ChevronRight, Globe,
  DollarSign, History, Loader2
} from 'lucide-react';
import QRCode from 'react-qr-code';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/layout/Layout';
import { toast } from 'sonner';

import { API_URL } from '../utils/apiConfig';

export default function BeneficiaryPortal() {
  const [searchParams] = useSearchParams();
  const [code, setCode] = useState(searchParams.get('code') || '');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [authorization, setAuthorization] = useState(null);
  const [allAuthorizations, setAllAuthorizations] = useState([]);
  const [summary, setSummary] = useState(null);
  const [activeTab, setActiveTab] = useState('lookup');
  
  const { language, toggleLanguage, setLanguage } = useLanguage();
  const { user, isAuthenticated } = useAuth();
  
  // Spanish-first for beneficiaries: If authenticated beneficiary and language not already Spanish, switch to Spanish
  useEffect(() => {
    if (isAuthenticated && user?.role === 'beneficiary' && language !== 'es') {
      // Check if user has preferred language set
      const preferredLang = user.preferred_language || 'es';
      if (preferredLang === 'es' || !user.preferred_language) {
        // Default to Spanish for beneficiaries
        setLanguage('es');
      }
    }
  }, [isAuthenticated, user, language, setLanguage]);
  
  const t = {
    en: {
      title: 'Beneficiary Portal',
      subtitle: 'Check your purchase authorizations',
      lookupTab: 'Look Up Code',
      historyTab: 'My Authorizations',
      enterCode: 'Enter your authorization code',
      codePlaceholder: 'Reference code (e.g., PECASH-ABC123)',
      lookupBtn: 'Look Up',
      orEnterPhone: 'Or check all your authorizations',
      phonePlaceholder: 'Your phone number',
      checkAllBtn: 'Check All',
      authFound: 'Authorization Found',
      from: 'From',
      colmado: 'Colmado',
      authorized: 'Authorized',
      redeemed: 'Redeemed',
      remaining: 'Remaining',
      expires: 'Expires',
      status: 'Status',
      showQR: 'Show this QR code at the colmado',
      backupCode: 'Backup Code',
      active: 'Active',
      completed: 'Completed',
      expired: 'Expired',
      partiallyRedeemed: 'Partially Redeemed',
      noAuths: 'No authorizations found',
      totalReceived: 'Total Received',
      totalRedeemed: 'Total Redeemed',
      availableNow: 'Available Now'
    },
    es: {
      title: 'Portal del Beneficiario',
      subtitle: 'Consulta tus autorizaciones de compra',
      lookupTab: 'Buscar Código',
      historyTab: 'Mis Autorizaciones',
      enterCode: 'Ingresa tu código de autorización',
      codePlaceholder: 'Código de referencia (ej: PECASH-ABC123)',
      lookupBtn: 'Buscar',
      orEnterPhone: 'O consulta todas tus autorizaciones',
      phonePlaceholder: 'Tu número de teléfono',
      checkAllBtn: 'Consultar Todo',
      authFound: 'Autorización Encontrada',
      from: 'De',
      colmado: 'Colmado',
      authorized: 'Autorizado',
      redeemed: 'Canjeado',
      remaining: 'Disponible',
      expires: 'Vence',
      status: 'Estado',
      showQR: 'Muestra este código QR en el colmado',
      backupCode: 'Código de Respaldo',
      active: 'Activo',
      completed: 'Completado',
      expired: 'Vencido',
      partiallyRedeemed: 'Parcialmente Canjeado',
      noAuths: 'No se encontraron autorizaciones',
      totalReceived: 'Total Recibido',
      totalRedeemed: 'Total Canjeado',
      availableNow: 'Disponible Ahora'
    }
  };
  
  const text = t[language] || t.en;
  
  const lookupCode = async () => {
    if (!code.trim()) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/beneficiary/lookup?code=${encodeURIComponent(code.trim())}`);
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Authorization not found');
      }
      
      const data = await response.json();
      setAuthorization(data);
    } catch (error) {
      toast.error(error.message);
      setAuthorization(null);
    } finally {
      setLoading(false);
    }
  };
  
  const checkAllAuthorizations = async () => {
    if (!phone.trim()) return;
    
    setLoading(true);
    try {
      // Get authorizations
      const authResponse = await fetch(`${API_URL}/api/beneficiary/authorizations?phone=${encodeURIComponent(phone.trim())}`);
      const authData = await authResponse.json();
      setAllAuthorizations(authData.authorizations || []);
      
      // Get summary
      const summaryResponse = await fetch(`${API_URL}/api/beneficiary/summary?phone=${encodeURIComponent(phone.trim())}`);
      const summaryData = await summaryResponse.json();
      setSummary(summaryData);
      
      setActiveTab('history');
    } catch (error) {
      toast.error(language === 'es' ? 'Error al consultar' : 'Failed to check');
    } finally {
      setLoading(false);
    }
  };
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
      case 'pending':
        return 'bg-emerald-100 text-emerald-700';
      case 'partially_redeemed':
        return 'bg-amber-100 text-amber-700';
      case 'completed':
        return 'bg-blue-100 text-blue-700';
      case 'expired':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-slate-100 text-slate-700';
    }
  };
  
  const getStatusText = (status) => {
    switch (status) {
      case 'active':
      case 'pending':
        return text.active;
      case 'partially_redeemed':
        return text.partiallyRedeemed;
      case 'completed':
        return text.completed;
      case 'expired':
        return text.expired;
      default:
        return status;
    }
  };
  
  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white py-12 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-emerald-100 mb-4">
              <ShoppingBag className="w-8 h-8 text-emerald-600" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900">{text.title}</h1>
            <p className="text-slate-600 mt-2">{text.subtitle}</p>
            
            {/* Language toggle */}
            <button 
              onClick={toggleLanguage}
              className="mt-4 inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700"
            >
              <Globe className="w-4 h-4" />
              {language === 'en' ? 'Español' : 'English'}
            </button>
          </div>
          
          {/* Tabs */}
          <div className="flex gap-2 mb-6">
            <Button
              variant={activeTab === 'lookup' ? 'default' : 'outline'}
              onClick={() => setActiveTab('lookup')}
              className={activeTab === 'lookup' ? 'bg-emerald-600' : ''}
            >
              <Search className="w-4 h-4 mr-2" />
              {text.lookupTab}
            </Button>
            <Button
              variant={activeTab === 'history' ? 'default' : 'outline'}
              onClick={() => setActiveTab('history')}
              className={activeTab === 'history' ? 'bg-emerald-600' : ''}
            >
              <History className="w-4 h-4 mr-2" />
              {text.historyTab}
            </Button>
          </div>
          
          {/* Lookup Tab */}
          {activeTab === 'lookup' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              {/* Code Lookup */}
              <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                <h2 className="font-semibold text-slate-900 mb-4">{text.enterCode}</h2>
                <div className="flex gap-2">
                  <Input
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                    placeholder={text.codePlaceholder}
                    className="flex-1"
                    data-testid="code-input"
                  />
                  <Button 
                    onClick={lookupCode} 
                    disabled={loading || !code.trim()}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="lookup-btn"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : text.lookupBtn}
                  </Button>
                </div>
              </div>
              
              {/* Phone Lookup */}
              <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                <h2 className="font-semibold text-slate-900 mb-4">{text.orEnterPhone}</h2>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder={text.phonePlaceholder}
                      className="pl-10"
                      data-testid="phone-input"
                    />
                  </div>
                  <Button 
                    onClick={checkAllAuthorizations} 
                    disabled={loading || !phone.trim()}
                    variant="outline"
                    data-testid="check-all-btn"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : text.checkAllBtn}
                  </Button>
                </div>
              </div>
              
              {/* Authorization Result */}
              {authorization && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-white rounded-2xl border border-emerald-200 p-6 shadow-sm"
                  data-testid="auth-result"
                >
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="font-semibold text-slate-900">{text.authFound}</h2>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(authorization.status)}`}>
                      {getStatusText(authorization.status)}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div>
                      <p className="text-sm text-slate-500">{text.from}</p>
                      <p className="font-medium text-slate-900">{authorization.supporter_name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">{text.colmado}</p>
                      <p className="font-medium text-slate-900">{authorization.merchant_name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">{text.authorized}</p>
                      <p className="font-medium text-slate-900">RD${authorization.dop_original_amount?.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">{text.remaining}</p>
                      <p className="font-bold text-emerald-600 text-lg">RD${authorization.dop_remaining_amount?.toLocaleString()}</p>
                    </div>
                  </div>
                  
                  {authorization.status !== 'expired' && authorization.status !== 'completed' && authorization.dop_remaining_amount > 0 && (
                    <div className="bg-slate-50 rounded-xl p-4 text-center">
                      <p className="text-sm text-slate-600 mb-3">{text.showQR}</p>
                      <div className="inline-block bg-white p-4 rounded-xl">
                        <QRCode value={authorization.qr_code_data || authorization.id} size={150} />
                      </div>
                      {authorization.numeric_code && (
                        <div className="mt-3">
                          <p className="text-xs text-slate-500">{text.backupCode}</p>
                          <p className="font-mono text-lg font-bold text-slate-900">{authorization.numeric_code}</p>
                        </div>
                      )}
                    </div>
                  )}
                  
                  <div className="mt-4 pt-4 border-t border-slate-100 text-sm text-slate-500">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      {text.expires}: {new Date(authorization.expires_at).toLocaleDateString()}
                    </div>
                  </div>
                </motion.div>
              )}
            </motion.div>
          )}
          
          {/* History Tab */}
          {activeTab === 'history' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              {/* Summary */}
              {summary && (
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
                    <DollarSign className="w-6 h-6 text-blue-500 mx-auto mb-2" />
                    <p className="text-xs text-slate-500">{text.totalReceived}</p>
                    <p className="font-bold text-slate-900">RD${summary.total_received_dop?.toLocaleString()}</p>
                  </div>
                  <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
                    <CheckCircle className="w-6 h-6 text-emerald-500 mx-auto mb-2" />
                    <p className="text-xs text-slate-500">{text.totalRedeemed}</p>
                    <p className="font-bold text-slate-900">RD${summary.total_redeemed_dop?.toLocaleString()}</p>
                  </div>
                  <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
                    <ShoppingBag className="w-6 h-6 text-amber-500 mx-auto mb-2" />
                    <p className="text-xs text-slate-500">{text.availableNow}</p>
                    <p className="font-bold text-emerald-600">RD${summary.total_remaining_dop?.toLocaleString()}</p>
                  </div>
                </div>
              )}
              
              {/* Authorization List */}
              {allAuthorizations.length === 0 ? (
                <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center">
                  <ShoppingBag className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">{text.noAuths}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {allAuthorizations.map((auth) => (
                    <div 
                      key={auth.id}
                      className="bg-white rounded-xl border border-slate-200 p-4 hover:border-emerald-300 transition-colors cursor-pointer"
                      onClick={() => {
                        setAuthorization(auth);
                        setActiveTab('lookup');
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                            <Store className="w-5 h-5 text-emerald-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{auth.merchant_name}</p>
                            <p className="text-sm text-slate-500">
                              {text.from} {auth.supporter_name}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-emerald-600">RD${auth.dop_remaining_amount?.toLocaleString()}</p>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(auth.status)}`}>
                            {getStatusText(auth.status)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Phone input if no data */}
              {!summary && (
                <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                  <h2 className="font-semibold text-slate-900 mb-4">{text.orEnterPhone}</h2>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder={text.phonePlaceholder}
                        className="pl-10"
                      />
                    </div>
                    <Button 
                      onClick={checkAllAuthorizations} 
                      disabled={loading || !phone.trim()}
                      className="bg-emerald-600 hover:bg-emerald-700"
                    >
                      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : text.checkAllBtn}
                    </Button>
                  </div>
                </div>
              )}
            </motion.div>
          )}
        </div>
      </div>
    </Layout>
  );
}
